import { useCallback, useMemo, useState } from 'react';

import { ProductCatalogV2, ProductSlug } from '../gql/generated/graphql';

import {
  DEFAULT_PACKAGE_CONFIGS,
  PackageConfig,
  PersistedPackageConfig,
  generatePackageDisplayName,
  getProductSignature,
  normalizeProductSlugs,
} from '../types/products';

const computeToggledConfigs = (
  prevConfigs: PackageConfig[],
  packageId: string,
  productSlug: ProductSlug,
): PackageConfig[] => {
  const targetConfig = prevConfigs.find((config) => config.id === packageId);
  if (!targetConfig) {
    return prevConfigs;
  }

  const isSelected = targetConfig.selectedProductSlugs.includes(productSlug);
  const currentTargetSlugs = normalizeProductSlugs(targetConfig.selectedProductSlugs);
  const toggledSlugs = normalizeProductSlugs(
    isSelected
      ? targetConfig.selectedProductSlugs.filter((slug) => slug !== productSlug)
      : [...targetConfig.selectedProductSlugs, productSlug],
  );
  const toggledSignature = getProductSignature(toggledSlugs);

  const duplicateConfig = prevConfigs.find(
    (config) =>
      config.id !== packageId &&
      getProductSignature(config.selectedProductSlugs) === toggledSignature,
  );

  if (duplicateConfig) {
    return prevConfigs.map((config) => {
      if (config.id === packageId) {
        return { ...config, selectedProductSlugs: toggledSlugs };
      }
      if (config.id === duplicateConfig.id) {
        return { ...config, selectedProductSlugs: currentTargetSlugs };
      }
      return config;
    });
  }

  return prevConfigs.map((config) =>
    config.id === packageId ? { ...config, selectedProductSlugs: toggledSlugs } : config,
  );
};

export const usePackageConfigs = (products: ProductCatalogV2[]) => {
  const getDefaultConfigs = () =>
    DEFAULT_PACKAGE_CONFIGS.map((config) => ({
      ...config,
      selectedProductSlugs: [...config.selectedProductSlugs],
    }));

  const [configs, setConfigs] = useState<PackageConfig[]>(getDefaultConfigs);

  const getConfigsWithNames = useCallback(
    (nextConfigs: PackageConfig[]) =>
      nextConfigs.map((config) => ({
        ...config,
        displayName: generatePackageDisplayName(config.selectedProductSlugs, products),
      })),
    [products],
  );

  const updateAllConfigs = useCallback((nextConfigs: PackageConfig[]) => {
    setConfigs(
      nextConfigs.map((config) => ({
        ...config,
        selectedProductSlugs: normalizeProductSlugs(config.selectedProductSlugs),
      })),
    );
  }, []);

  const hydrateConfigs = useCallback(
    (persistedConfigs: PersistedPackageConfig[]) => {
      if (!persistedConfigs.length) {
        return;
      }

      const persistedBySlot = new Map(
        persistedConfigs.map((config) => [
          config.slotIndex,
          normalizeProductSlugs(config.selectedProductSlugs),
        ]),
      );

      updateAllConfigs(
        DEFAULT_PACKAGE_CONFIGS.map((defaultConfig) => ({
          ...defaultConfig,
          selectedProductSlugs: persistedBySlot.get(defaultConfig.slotIndex) ?? [
            ...defaultConfig.selectedProductSlugs,
          ],
        })),
      );
    },
    [updateAllConfigs],
  );

  // Toggle a product in a specific package
  const toggleProduct = useCallback(
    (packageId: string, productSlug: ProductSlug): PackageConfig[] => {
      let result: PackageConfig[] = [];
      setConfigs((prev) => {
        result = computeToggledConfigs(prev, packageId, productSlug);
        return result;
      });
      return result;
    },
    [],
  );

  // Set all products for a specific package (replaces current selection)
  const setPackageProducts = useCallback((packageId: string, productSlugs: ProductSlug[]) => {
    setConfigs((prevConfigs) =>
      prevConfigs.map((config) => {
        if (config.id !== packageId) {
          return config;
        }

        return {
          ...config,
          selectedProductSlugs: normalizeProductSlugs(productSlugs),
        };
      }),
    );
  }, []);

  // Reset to default configurations
  const resetToDefaults = useCallback(() => {
    setConfigs(getDefaultConfigs());
  }, []);

  // Computed configs with updated display names based on current products
  const configsWithNames = useMemo(() => {
    return getConfigsWithNames(configs);
  }, [configs, getConfigsWithNames]);

  // Get a specific package config
  const getPackageConfig = useCallback(
    (packageId: string): PackageConfig | undefined => {
      return configsWithNames.find((c) => c.id === packageId);
    },
    [configsWithNames],
  );

  return {
    configs: configsWithNames,
    toggleProduct,
    setPackageProducts,
    updateAllConfigs,
    hydrateConfigs,
    resetToDefaults,
    getPackageConfig,
  };
};
